import { useState, useCallback, useRef, useEffect, RefObject } from "react";

interface useKOGImageZoomReturn {
	isFullscreen: boolean;
	scale: number;
	position: { x: number; y: number };
	isDragging: boolean;
	isCentering: boolean;
	imageRef: RefObject<HTMLImageElement | null>;
	containerRef: RefObject<HTMLDivElement | null>;
	openFullscreen: () => void;
	closeFullscreen: () => void;
	handleWheel: (e: React.WheelEvent) => void;
	handleMouseDown: (e: React.MouseEvent) => void;
	handleMouseMove: (e: React.MouseEvent) => void;
	handleMouseUp: () => void;
	handleOverlayClick: (e: React.MouseEvent) => void;
	handleTouchStart: (e: React.TouchEvent) => void;
	handleTouchMove: (e: React.TouchEvent) => void;
	handleTouchEnd: () => void;
	zoomIn: () => void;
	zoomOut: () => void;
}

export const useKOGImageZoom = (): useKOGImageZoomReturn => {
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [scale, setScale] = useState(1);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
	const [isCentering, setIsCentering] = useState(false);

	const imageRef = useRef<HTMLImageElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const MIN_SCALE = 1;
	const MAX_SCALE = 5;
	const SCALE_STEP = 0.3;

	const openFullscreen = useCallback(() => {
		setIsFullscreen(true);
		setScale(1);
		setPosition({ x: 0, y: 0 });
	}, []);

	const closeFullscreen = useCallback(() => {
		setIsFullscreen(false);
		setScale(1);
		setPosition({ x: 0, y: 0 });
		setIsDragging(false);
	}, []);

	const constrainPosition = useCallback((newX: number, newY: number, currentScale: number) => {
		const container = containerRef.current;
		const image = imageRef.current;

		if (!container || !image) return { x: newX, y: newY };

		const containerRect = container.getBoundingClientRect();
		const imageWidth = image.naturalWidth * currentScale;
		const imageHeight = image.naturalHeight * currentScale;

		const maxX = Math.max(0, (imageWidth - containerRect.width) / 2);
		const maxY = Math.max(0, (imageHeight - containerRect.height) / 2);

		return {
			x: Math.min(Math.max(newX, -maxX), maxX),
			y: Math.min(Math.max(newY, -maxY), maxY),
		};
	}, []);

	const calculateCenteredPosition = useCallback(
		(currentPosition: { x: number; y: number }, newScale: number) => {
			if (newScale <= MIN_SCALE) {
				return { x: 0, y: 0 };
			}

			const normalizedScale = (newScale - MIN_SCALE) / (MAX_SCALE - MIN_SCALE);
			const centeringFactor = Math.pow(normalizedScale, 0.8);

			return {
				x: currentPosition.x * centeringFactor,
				y: currentPosition.y * centeringFactor,
			};
		},
		[MIN_SCALE, MAX_SCALE],
	);

	const handleWheel = useCallback(
		(e: React.WheelEvent) => {

			const delta = e.deltaY > 0 ? -SCALE_STEP : SCALE_STEP;
			const newScale = Math.min(Math.max(scale + delta, MIN_SCALE), MAX_SCALE);

			if (newScale !== scale) {
				let newPosition = position;

				if (newScale > scale) {
					setIsCentering(false);
					const rect = containerRef.current?.getBoundingClientRect();
					if (rect) {
						const centerX = (e.clientX - rect.left - rect.width / 2) / scale;
						const centerY = (e.clientY - rect.top - rect.height / 2) / scale;

						const scaleRatio = newScale / scale;
						const newX = position.x - centerX * (scaleRatio - 1);
						const newY = position.y - centerY * (scaleRatio - 1);

						newPosition = { x: newX, y: newY };
					}
				} else if (newScale < scale) {
					setIsCentering(true);
					newPosition = calculateCenteredPosition(position, newScale);

					setTimeout(() => setIsCentering(false), 200);
				}

				const constrainedPosition = constrainPosition(newPosition.x, newPosition.y, newScale);
				setPosition(constrainedPosition);
				setScale(newScale);
			}
		},
		[scale, position, constrainPosition, calculateCenteredPosition],
	);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (scale > MIN_SCALE) {
				e.preventDefault();
				setIsDragging(true);
				setDragStart({ x: e.clientX, y: e.clientY });
				setLastPosition(position);
			}
		},
		[scale, position],
	);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (isDragging && scale > MIN_SCALE) {
				e.preventDefault();
				const deltaX = e.clientX - dragStart.x;
				const deltaY = e.clientY - dragStart.y;

				const newX = lastPosition.x + deltaX;
				const newY = lastPosition.y + deltaY;

				setPosition({ x: newX, y: newY });
			}
		},
		[isDragging, dragStart, lastPosition, scale],
	);

	const handleMouseUp = useCallback(() => {
		if (isDragging) {
			const constrainedPosition = constrainPosition(position.x, position.y, scale);
			setPosition(constrainedPosition);
		}
		setIsDragging(false);
	}, [isDragging, position, scale, constrainPosition]);

	const handleOverlayClick = useCallback(
		(e: React.MouseEvent) => {
			if (e.target === e.currentTarget) {
				closeFullscreen();
			}
		},
		[closeFullscreen],
	);

	const handleTouchStart = useCallback(
		(e: React.TouchEvent) => {
			if (e.touches.length === 1 && scale > MIN_SCALE) {
				const touch = e.touches[0];
				setIsDragging(true);
				setDragStart({ x: touch.clientX, y: touch.clientY });
				setLastPosition(position);
			}
		},
		[scale, position],
	);

	const handleTouchMove = useCallback(
		(e: React.TouchEvent) => {
			if (isDragging && e.touches.length === 1 && scale > MIN_SCALE) {
				e.preventDefault();
				const touch = e.touches[0];
				const deltaX = touch.clientX - dragStart.x;
				const deltaY = touch.clientY - dragStart.y;

				const newX = lastPosition.x + deltaX;
				const newY = lastPosition.y + deltaY;

				setPosition({ x: newX, y: newY });
			}
		},
		[isDragging, dragStart, lastPosition, scale],
	);

	const handleTouchEnd = useCallback(() => {
		if (isDragging) {
			const constrainedPosition = constrainPosition(position.x, position.y, scale);
			setPosition(constrainedPosition);
		}
		setIsDragging(false);
	}, [isDragging, position, scale, constrainPosition]);

	// Effect para detectar quando o mouse sai da janela do browser
	useEffect(() => {
		const handleGlobalMouseUp = () => {
			// Captura mouseup em qualquer lugar, incluindo fora da janela
			if (isDragging) {
				const constrainedPosition = constrainPosition(position.x, position.y, scale);
				setPosition(constrainedPosition);
				setIsDragging(false);
			}
		};

		const handleMouseLeave = (e: MouseEvent) => {
			if (
				isDragging &&
				(e.clientY <= 0 || e.clientX <= 0 || e.clientX >= window.innerWidth || e.clientY >= window.innerHeight)
			) {
				const constrainedPosition = constrainPosition(position.x, position.y, scale);
				setPosition(constrainedPosition);
				setIsDragging(false);
			}
		};

		const handleVisibilityChange = () => {
			if (document.hidden && isDragging) {
				const constrainedPosition = constrainPosition(position.x, position.y, scale);
				setPosition(constrainedPosition);
				setIsDragging(false);
			}
		};

		if (isFullscreen && isDragging) {
			document.addEventListener("mouseup", handleGlobalMouseUp, true);
			document.addEventListener("mousemove", handleMouseLeave, true);
			document.addEventListener("visibilitychange", handleVisibilityChange);

			window.addEventListener("blur", handleVisibilityChange);
		}

		return () => {
			document.removeEventListener("mouseup", handleGlobalMouseUp, true);
			document.removeEventListener("mousemove", handleMouseLeave, true);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			window.removeEventListener("blur", handleVisibilityChange);
		};
	}, [isFullscreen, isDragging, position, scale, constrainPosition]);

	const zoomIn = useCallback(() => {
		const newScale = Math.min(scale + SCALE_STEP, MAX_SCALE);
		if (newScale !== scale) {
			setIsCentering(true);
			const newPosition = calculateCenteredPosition(position, newScale);
			const constrainedPosition = constrainPosition(newPosition.x, newPosition.y, newScale);
			setPosition(constrainedPosition);
			setScale(newScale);
			setTimeout(() => setIsCentering(false), 200);
		}
	}, [scale, position, constrainPosition, calculateCenteredPosition]);

	const zoomOut = useCallback(() => {
		const newScale = Math.max(scale - SCALE_STEP, MIN_SCALE);
		if (newScale !== scale) {
			setIsCentering(true);
			const newPosition = calculateCenteredPosition(position, newScale);
			const constrainedPosition = constrainPosition(newPosition.x, newPosition.y, newScale);
			setPosition(constrainedPosition);
			setScale(newScale);
			setTimeout(() => setIsCentering(false), 200);
		}
	}, [scale, position, constrainPosition, calculateCenteredPosition]);

	return {
		isFullscreen,
		scale,
		position,
		isDragging,
		isCentering,
		imageRef,
		containerRef,
		openFullscreen,
		closeFullscreen,
		handleWheel,
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		handleOverlayClick,
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
		zoomIn,
		zoomOut,
	};
};
